import { useEffect, useRef, useState } from 'react';
import { LogOut, RefreshCw, Send } from 'lucide-react';
import { User } from 'firebase/auth';
import { toast } from 'sonner';

import RyeLogo from 'components/Logo';
import { Button } from 'components/ui/button';
import { Card, CardTitle } from 'components/ui/card';
import { Typography } from 'components/ui/typography';
import { useAuth } from 'providers/AuthProvider';
import { Routes } from 'config/constants';

interface EmailVerificationGateProps {
  user: User;
  autoSendOnMount?: boolean;
  onVerified?: () => void;
}

const RESEND_COOLDOWN_SECONDS = 60;

export function EmailVerificationGate({
  user,
  autoSendOnMount = false,
  onVerified,
}: EmailVerificationGateProps) {
  const { logout, reloadUser, sendVerificationEmail } = useAuth();
  const [checking, setChecking] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const autoSent = useRef(false);

  const handleSendVerification = async () => {
    if (resendCooldownSeconds > 0) {
      return false;
    }

    setSending(true);
    const result = await sendVerificationEmail(user);
    setSending(false);

    if (!result.ok) {
      toast.error(result.error);
      return false;
    }

    setSent(true);
    setResendCooldownSeconds(RESEND_COOLDOWN_SECONDS);
    toast.success('Verification email sent');
    return true;
  };

  useEffect(() => {
    if (!autoSendOnMount || autoSent.current) {
      return;
    }

    autoSent.current = true;
    void handleSendVerification();
    // Run once per mounted gate. handleSendVerification intentionally stays out
    // of the dependency list so a re-render does not resend the email.
  }, [autoSendOnMount]);

  useEffect(() => {
    if (resendCooldownSeconds <= 0) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setResendCooldownSeconds((seconds) => Math.max(seconds - 1, 0));
    }, 1000);

    return () => window.clearTimeout(timeoutId);
  }, [resendCooldownSeconds]);

  const handleRefreshVerification = async () => {
    setChecking(true);
    const result = await reloadUser(user);
    setChecking(false);

    if (!result.ok) {
      toast.error(result.error);
      return;
    }

    if (!result.data.emailVerified) {
      toast.error('Email is not verified yet');
      return;
    }

    toast.success('Email verified');
    onVerified?.();
  };

  const emailLabel = user.email ?? 'your email address';
  const resendDisabled = sending || resendCooldownSeconds > 0;

  return (
    <main className="relative flex min-h-screen w-full items-center justify-center bg-[var(--bg)] px-6 py-10">
      <div className="absolute inset-0 z-0 bg-[linear-gradient(var(--grid-line-strong)_1px,transparent_1px),linear-gradient(90deg,var(--grid-line-strong)_1px,transparent_1px)] bg-[length:32px_32px] bg-[-1px_-1px] opacity-55 [mask-image:radial-gradient(ellipse_60%_60%_at_50%_35%,#000,transparent_80%)]" />
      <Card className="relative z-10 w-full max-w-[400px] p-8">
        <div className="space-y-8">
          <RyeLogo to={Routes.Login} />
          <div className="space-y-3">
            <CardTitle>Check your email</CardTitle>
            <Typography variant="body" as="p" className="text-[var(--text-muted)]">
              {sent
                ? `We sent a new verification link to ${emailLabel}.`
                : `We sent a verification link to ${emailLabel}.`}
            </Typography>
            <Typography variant="body" as="p" className="text-[var(--text-muted)]">
              Open the link, then return here to continue.
            </Typography>
          </div>
          <div className="space-y-4">
            <div className="grid gap-2 sm:grid-cols-2">
              <Button
                type="button"
                variant="secondary"
                className="w-full"
                disabled={checking}
                onClick={handleRefreshVerification}
              >
                <RefreshCw className="h-4 w-4" />
                {checking ? 'Checking...' : 'I verified'}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                disabled={resendDisabled}
                onClick={handleSendVerification}
              >
                <Send className="h-4 w-4" />
                {sending
                  ? 'Sending...'
                  : resendCooldownSeconds > 0
                  ? `Resend in ${resendCooldownSeconds}s`
                  : 'Resend email'}
              </Button>
            </div>
            <div className="flex justify-center">
              <Button
                type="button"
                variant="link"
                className="h-auto px-2 py-1 text-[var(--text-muted)]"
                onClick={logout}
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </main>
  );
}
